import { useAtom } from "jotai";
import { forwardRef, FC, useState, useRef, useEffect } from "react";
import { TouchableOpacity, View, StyleSheet } from "react-native";
import { globalFullscreenAtom } from "../../../../../atoms";
import RScrollView from "../../../../RScrollView";
import variables from "../../../../../styles/variables";
import RText from "../../../../RText";
import { useResponsive } from "../../../../../hooks/useResponsive";

/**
 * RacecardAndResultWidgetTabs is a simplified Tabs component used in the Racecard and Result Widget
 * when there are multiple days
 *
 * The tabs display the date (e.g. "MONDAY") and allow switching between different days
 * The component handles dynamic tab widths
 *
 * The styles are simplified compared to the main Tabs component to fit the design of the Racecard and Result Widget
 * The component expects an array of items, where each item has a label (for the tab) and a child (the content to display when the tab is selected)
 * The widths of the tabs are calculated on layout and stored in the items array to enable scrolling to the correct position when a tab is selected
 *
 * @param {array} items - an array of objects with 'label' and 'child' properties for each tab
 * @param {number} selectedIndex - the index of the currently selected tab
 * @param {function} onTabChange - a callback function that is called when a tab is pressed, with the new index as an argument
 *
 * @returns {JSX.Element} The rendered tabs component
 */

const { colors } = variables;

type TabProps = {
  items: any[];
  selectedIndex: number;
  onTabChange?: (index: number) => void;
};

const RacecardAndResultWidgetTabs: FC<TabProps> = forwardRef(
  ({ items, selectedIndex, onTabChange }, ref) => {
    const [isGlobalFullScreen] = useAtom(globalFullscreenAtom);
    const { isSmallAndDown, isMediumAndUp } = useResponsive();

    const selected = selectedIndex;
    const scrollRef = useRef(null);
    const [widthsReady, setWidthsReady] = useState(false);

    useEffect(() => {
      const LEFT_GAP = 30; //how much of the left hand tab to show
      const selectedPositionX = items.slice(0, selected).reduce((a, b) => {
        return a + b.width || 0;
      }, 0);

      scrollRef.current?.scrollTo({
        y: 0,
        x: selectedPositionX - LEFT_GAP,
        animated: true,
      });
    }, [selected, widthsReady]);

    return (
      <View>
        <View style={{ paddingTop: 16, paddingBottom: 8 }}>
          <RScrollView
            scrollRef={scrollRef}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ flexGrow: 1 }}
            horizontal
            elementDistance={120}
            childrenWidth={items.length * 110 + 10}
            hideArrows
          >
            {items.map((item, index) => (
              <TouchableOpacity
                key={`tabs-${index}`}
                style={[
                  styles.tab,
                  index === selected && styles.tabSelected,
                  index === 0 && {
                    borderTopLeftRadius: 5,
                    borderBottomLeftRadius: 5,
                  },
                  index === items.length - 1 && {
                    borderTopRightRadius: 5,
                    borderBottomRightRadius: 5,
                  },
                  {
                    paddingHorizontal: isMediumAndUp ? 15 : 10,
                    display: isGlobalFullScreen ? "none" : "flex",
                  },
                ]}
                onLayout={(event) => {
                  // store the width of the tab, so we can scroll to it on select
                  items[index]["width"] = event.nativeEvent.layout.width;
                  // flag when all widths are set, so we can finally scroll
                  if (items.every((item) => item.width)) {
                    setWidthsReady(true);
                  }
                }}
                onPress={() => onTabChange && onTabChange(index)}
              >
                <RText
                  family="secondary"
                  color={index === selected ? "white" : "primary"}
                  numberOfLines={1}
                  weight="semiBold"
                  uppercase
                  lineHeight={30}
                  ellipsizeMode="tail"
                  style={{ maxWidth: isSmallAndDown ? 80 : undefined }}
                >
                  {item.label?.includes("-")
                    ? item.label?.replace("-", " ")
                    : item.label}
                </RText>
              </TouchableOpacity>
            ))}
          </RScrollView>
        </View>
        <View>{items[selected]?.child}</View>
      </View>
    );
  }
);

const styles = StyleSheet.create({
  tab: {
    padding: 2,
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    borderWidth: 1,
    borderColor: colors.palette.rtv.secondary,
  },
  tabSelected: {
    zIndex: 1,
    backgroundColor: colors.palette.rtv.secondary,
  },
});

export default RacecardAndResultWidgetTabs;
